import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Video,
  UserRound,
  Building2,
  Layers,
  BookText,
  Atom,
  FlaskConical,
  GraduationCap,
  Archive,
  Settings,
  Blocks,
  Clapperboard,
  PackageOpen,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useSideRail } from "@/contexts/SideRailContext";
import { useEffect } from "react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useQuery } from "@tanstack/react-query";
import { HOBBES_ICON_URL } from "@/constants/brandAssets";
import { FeatureFlags } from "@/constants/constants";
import { useOrganization } from "@/hooks/useOrganization";
import knowledgeGapsService from "@/lib/api/knowledgeGaps";
import { cn } from "@/lib/utils";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
  SidebarHeader,
  SidebarRail,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { OrganizationSwitcher } from "@/components/organization/OrganizationSwitcher";

interface NavItem {
  title: string;
  icon: LucideIcon;
  path: string;
  featureFlag?: FeatureFlags;
  /** Optional sidebar badge — currently used to surface active knowledge gaps. */
  badge?: { count: number; label?: string };
}

interface NavGroup {
  label?: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    items: [
      { title: "Home", icon: LayoutDashboard, path: "/" },
    ],
  },
  {
    label: "Insights",
    items: [
      { title: "Sessions", icon: Video, path: "/analytics/sessions" },
      { title: "People", icon: UserRound, path: "/people" },
      { title: "Accounts", icon: Building2, path: "/accounts" },
      { title: "Performance", icon: Atom, path: "/performance", featureFlag: FeatureFlags.SHOW_PERFORMANCE },
    ],
  },
  {
    label: "Agent",
    items: [
      { title: "Agent Versions", icon: Layers, path: "/agents" },
      { title: "Agent Knowledge", icon: GraduationCap, path: "/agent-knowledge" },
      { title: "Agent Assets", icon: BookText, path: "/content-library" },
    ],
  },
  {
    label: "Content",
    items: [
      { title: "Source Library", icon: Archive, path: "/source-library" },
      { title: "Context Packs", icon: PackageOpen, path: "/context-packs" },
      { title: "Product Studio", icon: Clapperboard, path: "/demo-studio" },
    ],
  },
  {
    label: "Evaluation",
    items: [
      { title: "Simulations", icon: FlaskConical, path: "/evaluation/simulations" },
      { title: "Evaluators", icon: Atom, path: "/evaluation/evaluators" },
    ],
  },
];

const footerItems: NavItem[] = [
  { title: "Settings", icon: Settings, path: "/organization-settings" },
  { title: "Integrations", icon: Blocks, path: "/integrations" },
];

export function ModernSidebar() {
  const location = useLocation();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const { toggleSideRail } = useSideRail();
  const showPerformance = useFeatureFlagEnabled(FeatureFlags.SHOW_PERFORMANCE);
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  // Active unresolved-gap count for the Agent Knowledge sidebar badge. Driven
  // off the same /report endpoint the page uses, so the cached entry is
  // shared and we add no additional request when the user is on the page.
  // Refetched every 60s — frequent enough to feel live, infrequent enough
  // not to spam the backend.
  const { data: kgReport } = useQuery({
    queryKey: ["knowledge-gaps-report", orgId],
    queryFn: async () => {
      if (!orgId) return null;
      const res = await knowledgeGapsService.getReport(orgId);
      return res.success && res.data ? res.data : null;
    },
    enabled: !!orgId,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const activeGapCount = kgReport?.total_active_summaries ?? 0;

  const flagValues: Record<string, boolean | undefined> = {
    [FeatureFlags.SHOW_PERFORMANCE]: showPerformance,
  };

  const filteredNavGroups = navGroups.map(group => ({
    ...group,
    items: group.items
      .filter(item => !item.featureFlag || flagValues[item.featureFlag])
      .map(item =>
        item.path === "/agent-knowledge" && activeGapCount > 0
          ? { ...item, badge: { count: activeGapCount, label: `${activeGapCount} active gaps` } }
          : item,
      ),
  }));

  // Handle "/" keyboard shortcut to toggle Ask Hobbes side rail
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) {
        e.preventDefault();
        toggleSideRail();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleSideRail]);

  const isCurrentPath = (path: string) => {
    if (path === "/") {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isFooterActive = (path: string) => {
    if (path === "/organization-settings") {
      return location.pathname.startsWith("/organization-settings") ||
        location.pathname.startsWith("/account-settings");
    }
    return isCurrentPath(path);
  };

  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const handleAskHobbesClick = () => {
    toggleSideRail();
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const showLabels = isMobile || state === "expanded";

  const menuButtonClass = cn(
    "rounded-md transition-colors duration-150 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground",
    showLabels
      ? "h-8 w-full justify-start px-2.5 text-sm"
      : "h-8 w-8 justify-center overflow-visible px-0",
  );

  const menuLinkClass = cn(
    "relative flex items-center",
    showLabels ? "h-8 w-full justify-start gap-3" : "h-8 w-8 justify-center",
  );

  const askHobbesClass = cn(
    menuButtonClass,
    "border border-transparent text-sidebar-foreground shadow-none hover:text-sidebar-foreground",
  );

  return (
    <Sidebar collapsible="icon" variant="floating">
      <SidebarHeader className={cn("h-11 shrink-0 justify-center border-b border-sidebar-border py-0", showLabels ? "px-3" : "items-center px-2")}>
        <div className={cn("flex w-full items-center", showLabels ? "gap-2" : "justify-center")}>
          <div className={cn("min-w-0", showLabels ? "flex-1" : "w-8")}>
            <OrganizationSwitcher />
          </div>
          {showLabels && <SidebarTrigger className="h-8 w-8 shrink-0" />}
        </div>
      </SidebarHeader>

      <SidebarContent className={cn("gap-0 py-4 group-data-[collapsible=icon]:!px-2", showLabels ? "px-[11px]" : "px-2")}>
        <SidebarGroup className="px-0 py-0">
          <SidebarGroupContent>
            <SidebarMenu className={cn(showLabels ? "items-stretch gap-0" : "items-center gap-0.5")}>
              <SidebarMenuButton
                className={askHobbesClass}
                style={{
                  background: "linear-gradient(hsl(var(--sidebar-background)), hsl(var(--sidebar-background))) padding-box, linear-gradient(90deg, #2563eb 0%, #38bdf8 100%) border-box",
                }}
                onClick={handleAskHobbesClick}
                tooltip="Ask Hobbes"
              >
                <img
                  src={HOBBES_ICON_URL}
                  alt=""
                  className="h-4 w-4 shrink-0 dark:invert"
                />
                <span className={showLabels ? "truncate" : "sr-only"}>Ask Hobbes</span>
              </SidebarMenuButton>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {filteredNavGroups.map((group, groupIdx) => (
          <SidebarGroup
            key={group.label ?? groupIdx}
            className={cn(
              "px-0 py-0",
              showLabels ? "mt-5" : groupIdx === 0 ? "mt-3" : "",
            )}
          >
            {groupIdx > 0 && (
              <div className={cn("h-px bg-sidebar-border", showLabels ? "hidden" : "mx-auto my-2 w-8")} />
            )}
            {showLabels && group.label && (
              <div className="h-4 px-2.5 text-xs font-medium leading-4 text-muted-foreground">
                {group.label}
              </div>
            )}
            <SidebarGroupContent>
              <SidebarMenu className={cn(showLabels ? cn("items-stretch gap-0", group.label && "mt-2") : "items-center gap-0.5")}>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isCurrentPath(item.path)}
                      className={menuButtonClass}
                      tooltip={item.title}
                    >
                      <Link
                        to={item.path}
                        className={menuLinkClass}
                        aria-label={item.badge && item.badge.count > 0 ? `${item.title}, ${item.badge.label}` : item.title}
                        onClick={handleNavClick}
                      >
                        <item.icon
                          size={showLabels ? 16 : 15}
                          strokeWidth={isCurrentPath(item.path) ? 2 : 1.5}
                          className={cn("shrink-0", isCurrentPath(item.path)
                            ? "text-sidebar-accent-foreground"
                            : "text-sidebar-foreground")}
                        />
                        <span className={showLabels ? "truncate" : "sr-only"}>{item.title}</span>
                        {item.badge && item.badge.count > 0 && (
                          <span
                            className={cn(
                              "flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-semibold leading-none text-primary-foreground",
                              showLabels ? "ml-auto" : "absolute -right-0.5 -top-1",
                            )}
                            title={item.badge.label}
                          >
                            {item.badge.count > 9 ? '9+' : item.badge.count}
                          </span>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className={cn("border-t border-sidebar-border py-3 group-data-[collapsible=icon]:!px-2", isMobile ? "px-3" : "px-2")}>
        <SidebarGroup className="px-0 py-0">
          <SidebarGroupContent>
            <SidebarMenu className={cn(showLabels ? "items-stretch gap-1" : "items-center gap-0.5")}>
              {footerItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={isFooterActive(item.path)}
                    className={menuButtonClass}
                    tooltip={item.title}
                  >
                    <Link
                      to={item.path}
                      className={menuLinkClass}
                      onClick={handleNavClick}
                    >
                      <item.icon
                        size={showLabels ? 16 : 15}
                        strokeWidth={isFooterActive(item.path) ? 2 : 1.5}
                        className={cn("shrink-0", isFooterActive(item.path) ? "text-sidebar-accent-foreground" : "text-sidebar-foreground")}
                      />
                      <span className={showLabels ? "truncate" : "sr-only"}>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
